import { ICRMUser } from './crm-user';
import { ICustomerUser } from './customer.dto';

/** Order-level requisition actions are available during either lab waiting stage. */
export function isLabWaitingStage(status?: string): boolean {
  return status === 'awaiting_requisition' || status === 'awaiting_results';
}

// Base64 expands a 3MB PDF to 4MB, leaving room under emr-api's 5MB JSON limit.
export const MAX_LAB_REQ_PDF_BYTES = 3 * 1024 * 1024;

export interface LabReqTestElement {
  label?: string;
  children?: Array<LabReqTestOption>;
}

export interface LabReqTestOption {
  type: 'string' | 'number' | 'boolean' | 'list';
  id: string;
  label: string;
  description?: string;
  required?: boolean;
  children?: Array<LabReqTestOption>;
  elements?: Array<LabReqTestElement>;
}
export interface LabReqTest {
  id: string;
  label: string;
  options: Array<LabReqTestOption>;
}

export interface LabReqTemplate {
  id: string;
  label: string;
  description: string;
  brand: string;
  allowAdditionalTests?: boolean;
  tests?: Array<LabReqTest>;
  v?: number;
  variant?: string;
}

export interface LabReqField {
  id: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'list' | 'divider' | 'break';
  required?: boolean;
  level?: 'parent' | 'child' | undefined;
  elements?: Array<LabReqTestElement>;
  children?: Array<LabReqField>;
  description?: string;
}

export interface ILabReq {
  _id: string;
  brand: string;
  customer: string | ICustomerUser;
  adminUser: string | ICRMUser;
  templateId: string;
  templateVariant?: string;
  data: { [key: string]: string | number };
  /** Integration-specific linkage, e.g. Beluga's `{ source, masterId, orderId }`. */
  meta?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
  dueDate: Date;
  pdfKey?: string;
  v?: number;
}

export type CreateLabReqDTO = {
  organization: string;
  customer: string;
  adminUser?: string;
  templateId?: string;
  templateVariant?: string;
  data?: Object;
  meta?: Record<string, any>;
  dueDate?: Date;
  v?: number;
  pdfKey?: string;
};


export type ILabReqDTO = Partial<
  Omit<ILabReq, '_id' | 'createdAt' | 'updatedAt'>
>;

export const UPLOADED_LAB_REQ_LABEL = 'Lab Req';

export type UploadLabReqDTO = {
  customerId: string;
  file: { buffer: string; name?: string; mimetype?: string };
  dueDate?: string | Date;
  /** Label for the list, since an uploaded req has no template. Defaults to `UPLOADED_LAB_REQ_LABEL`. */
  templateId?: string;
};

export interface LabReqPdfUrlResponse {
  success: boolean;
  signedUrl: string;
  expiresIn: number;
  message: string;
}

export interface LabReqQueryParams {
  id?: string;
  minCreatedDate?: string | Date;
  maxCreatedDate?: string | Date;
  minUpdatedDate?: string | Date;
  maxUpdatedDate?: string | Date;
  minDueDate?: string | Date;
  maxDueDate?: string | Date;
  customer?: string;
  customerName?: string;
  templateId?: string;
  templateVariant?: string;
  limit?: string | number;
  offset?: string | number;
  sortField?: string;
  sortOrder?: string | number;
}
